// Generic "artifact" concept — an agent-produced report surfaced in the chat
// as a compact card and rendered in the right-side report panel.
//
// Nothing here is agent-specific. A new report type only needs: (1) an Artifact
// with the right `type`, and (2) a renderer registered under that type in
// ARTIFACT_RENDERERS (see app.config.ts). The chat and the panel never change.
import { InjectionToken, Type } from '@angular/core';

export interface Artifact {
  /** Stable within a page session — `${sessionId}:${msgId}`. Used by the panel
   *  to know which artifact is active and by the card to toggle it. */
  id: string;
  /** Registry key into ARTIFACT_RENDERERS. 'markdown' is the only one shipped. */
  type: string;
  /** Shown on the card and in the panel header (e.g. "Account Summary"). */
  title: string;
  /** PrimeIcon class for the card + header (e.g. 'pi-sparkles'). */
  icon?: string;
  /** Originating agent_id — provenance, drives the default icon when unset. */
  agentId?: string;
  /** Markdown body — what the 'markdown' renderer displays in the panel. */
  body?: string;
  /** Presigned URL to a generated document. Drives "Open in new tab" / "Download". */
  fileUrl?: string;
  /** Suggested filename for the download action. */
  fileName?: string;
  /**
   * NEAT only: the uploaded transcript's filename this report was generated
   * from. Drives the "Reanalyze" button — re-sent with force_reanalyze so the
   * runtime re-reads the same S3 file and bypasses its analysis cache.
   */
  uploadFilename?: string;
  /**
   * How much of the originating message's text to show in the chat bubble
   * (the report itself always renders in the panel):
   *  - 'full'     — show all of msg.content (default; e.g. NEAT's chat fragment).
   *  - 'preamble' — show only the conversational lead-in before the report body
   *                 (e.g. SF: "Here is the full account summary for X:").
   *  - 'none'     — show nothing; the card is the only in-chat element.
   */
  inlineContent?: 'full' | 'preamble' | 'none';
}

/** Inputs every artifact-renderer component receives from the panel. */
export interface ArtifactRendererInputs {
  artifact: Artifact;
}

/**
 * Maps an Artifact.type to the component that renders its body in the panel.
 * Provided once in app.config.ts; the panel resolves the component via
 * NgComponentOutlet. This is the single extension point for new report types.
 */
export const ARTIFACT_RENDERERS =
  new InjectionToken<Record<string, Type<unknown>>>('ARTIFACT_RENDERERS');
